import { useEffect, useState } from "react";
import { ServiceIcon } from "@/components/ServiceIcon";
import { useSiteContent } from "@/hooks/useSiteContent";

type Service = {
  id: string;
  title: string;
  description: string;
  price: string | null;
  icon: string;
};

const FALLBACK_SERVICES: Service[] = [
  { id: "fb-ac", title: "AC Service & Repair", description: "Installation, gas refill, deep cleaning and repair for all AC brands.", price: "From ₹499", icon: "wind" },
  { id: "fb-wm", title: "Washing Machine Repair", description: "Top & front load repair, motor, drum and PCB issues fixed at home.", price: "From ₹399", icon: "shirt" },
  { id: "fb-fr", title: "Fridge Repair", description: "Cooling, gas charging, compressor and thermostat repair for all brands.", price: "From ₹399", icon: "refrigerator" },
  { id: "fb-ro", title: "RO Service", description: "Filter replacement, full service and installation of RO water purifiers.", price: "From ₹299", icon: "droplet" },
];

export function Services() {
  const [services, setServices] = useState<Service[]>(FALLBACK_SERVICES);
  const { content } = useSiteContent();

  useEffect(() => {
    let cancelled = false;
    let attempt = 0;
    const load = async () => {
      const { supabase } = await import("@/integrations/supabase/client");
      while (!cancelled && attempt < 5) {
        const { data, error } = await supabase
          .from("services")
          .select("id,title,description,price,icon")
          .eq("visible", true)
          .order("sort_order");
        if (!cancelled && data && data.length) {
          setServices(data);
          return;
        }
        if (!error) return;
        attempt++;
        await new Promise((r) => setTimeout(r, 800 * attempt));
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  return (
    <section id="services" className="py-20 md:py-24">
      <div className="container">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-sm font-semibold uppercase tracking-wider text-accent">Our services</p>
          <h2 className="mt-2 text-3xl md:text-4xl text-balance">Repair for every appliance in your home</h2>
          <p className="mt-4 text-muted-foreground">Fast, reliable service across Mansa. Call us or book online.</p>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {services.map((s) => (
            <div
              key={s.id}
              className="group relative overflow-hidden rounded-2xl border border-border bg-card p-6 shadow-card-soft transition-smooth hover:-translate-y-1 hover:shadow-elegant"
            >
              <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-primary text-primary-foreground shadow-glow transition-smooth group-hover:scale-110">
                <ServiceIcon name={s.icon} className="h-7 w-7" />
              </div>
              <h3 className="text-xl">{s.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{s.description}</p>
              {s.price && <p className="mt-4 text-sm font-semibold text-primary">{s.price}</p>}
              <a href="#book" className="mt-4 inline-block text-sm font-semibold text-accent hover:underline">Book now →</a>
            </div>
          ))}
        </div>
        <p className="mt-10 text-center text-sm text-muted-foreground">
          Contact us directly: {content.shop_phone}
        </p>
      </div>
    </section>
  );
}
